import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import RecipeCard from "../components/RecipeCard";
import { Loader2, Search, X } from "lucide-react";

const seasonFilters = ["all", "spring", "summer", "autumn", "winter"];
const courseFilters = ["all", "amuse-bouche", "palate-cleanser", "starter", "main", "dessert"];
const sourceFilters = [
  { value: "all", label: "All" },
  { value: "ai", label: "Kitchen" },
  { value: "community", label: "Readers" },
];

// Regional keyword map — matches any of the terms against a recipe's `region` string,
// case-insensitively. Handles the fact that recipe.region often says "Kerala" not "India".
const REGIONS = [
  { value: "all", label: "All", keywords: null },
  { value: "scotland", label: "Scotland", keywords: ["scot", "highland", "hebrides", "mull", "orkney", "shetland"] },
  { value: "england", label: "England", keywords: ["england", "english", "london", "yorkshire", "cornwall", "cotswold", "buckinghamshire", "lake district", "devon"] },
  { value: "ireland", label: "Ireland", keywords: ["ireland", "irish"] },
  { value: "france", label: "France", keywords: ["france", "french", "paris", "loire", "provence", "burgundy", "brittany", "béarn", "lille", "bayonne"] },
  { value: "spain", label: "Spain", keywords: ["spain", "spanish", "galicia", "galician", "basque", "bilbao", "san sebast", "pontevedra", "coruña", "vigo", "lugo", "ourense", "compostela", "rías baixas", "costa da morte", "tolosa", "pamplona", "andaluc", "catalan", "catalonia", "valencia", "madrid", "seville", "barcelona"] },
  { value: "italy", label: "Italy", keywords: ["italy", "italian", "modena", "tuscan", "tuscany", "sicily", "sicilian", "rome", "roman", "veneto", "venice", "venetian", "burano", "treviso", "piedmont", "piedmontese", "lombardy", "milan", "naples", "neapolitan", "bologna", "emilia"] },
  { value: "greece-cyprus", label: "Greece & Cyprus", keywords: ["greek", "greece", "athens", "crete", "cretan", "cyprus", "cypriot", "nicosia", "santorini"] },
  { value: "turkey", label: "Turkey", keywords: ["turkey", "turkish", "istanbul", "izmir", "adana", "anatol", "ottoman"] },
  { value: "georgia", label: "Georgia", keywords: ["georgia,", "georgian", "tbilisi", "kakheti", "adjara"] },
  { value: "ethiopia", label: "Ethiopia", keywords: ["ethiopia", "ethiopian", "addis", "gondar"] },
  { value: "india", label: "India", keywords: ["india", "kerala", "bengal", "kashmir", "punjab", "goa", "tamil", "karnataka", "hyderabad", "delhi", "mumbai", "lucknow", "chettinad", "mysore"] },
  { value: "japan", label: "Japan", keywords: ["japan", "tokyo", "kyoto", "osaka"] },
  { value: "vietnam", label: "Vietnam", keywords: ["vietnam", "ho chi minh", "hanoi", "mekong"] },
  { value: "mexico", label: "Mexico", keywords: ["mexico", "oaxaca", "puebla", "yucatán"] },
  { value: "nordic", label: "Nordic", keywords: ["denmark", "copenhagen", "sweden", "norway", "nordic", "scandinav"] },
  { value: "austria", label: "Austria", keywords: ["austria", "vienna", "salzkammergut", "styria"] },
  { value: "levant", label: "Levant", keywords: ["levant", "jerusalem", "lebanon", "syria", "beirut"] },
];

const cravings = ["mackerel", "venison", "mushroom", "chocolate", "curry", "risotto"];

const matchesRegion = (recipe, regionValue) => {
  if (regionValue === "all") return true;
  const region = REGIONS.find((r) => r.value === regionValue);
  if (!region?.keywords) return true;
  const hay = (recipe.region || "").toLowerCase();
  return region.keywords.some((k) => hay.includes(k));
};

const Library = () => {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [season, setSeason] = useState("all");
  const [course, setCourse] = useState("all");
  const [source, setSource] = useState("all");
  const [region, setRegion] = useState("all");
  const [veganOnly, setVeganOnly] = useState(false);
  const [vegetarianOnly, setVegetarianOnly] = useState(false);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [visibleCap, setVisibleCap] = useState(48);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (season !== "all") params.append("season", season);
    if (course !== "all") params.append("course", course);
    if (source !== "all") params.append("source", source);
    if (veganOnly) params.append("is_vegan", "true");
    else if (vegetarianOnly) params.append("is_vegetarian", "true");
    if (debouncedQ) params.append("q", debouncedQ);
    params.append("limit", "500");
    api
      .get(`/recipes?${params.toString()}`)
      .then((res) => setRecipes(res.data))
      .catch(() => setRecipes([]))
      .finally(() => setLoading(false));
  }, [season, course, source, veganOnly, vegetarianOnly, debouncedQ]);

  // Reset the visible cap whenever the filter set changes
  useEffect(() => {
    setVisibleCap(48);
  }, [season, course, source, region, veganOnly, vegetarianOnly, debouncedQ]);

  const visibleRecipes = region === "all" ? recipes : recipes.filter((r) => matchesRegion(r, region));
  // Load-More guard kicks in once the filtered set exceeds 150
  const usePagination = visibleRecipes.length > 150;
  const shownRecipes = usePagination ? visibleRecipes.slice(0, visibleCap) : visibleRecipes;

  return (
    <div className="min-h-screen pt-20 sm:pt-24 pb-20 sm:pb-32 bg-stone-950">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-10">
        <div className="mb-12 border-b border-stone-800 pb-10">
          <div className="flex items-center gap-4 mb-6">
            <span className="magazine-number text-5xl">03</span>
            <div className="w-16 h-px bg-orange-600" />
            <span className="eyebrow">The Library</span>
          </div>
          <div className="flex items-end justify-between flex-wrap gap-8">
            <h1 className="font-serif-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-stone-100 leading-[0.95] sm:leading-[0.9] tracking-tight sm:tracking-tighter">
              Every dish, <span className="italic text-orange-600">on the shelf</span>.
            </h1>
            <div className="text-sm text-stone-500 font-mono-accent">
              {loading ? "..." : visibleRecipes.length} in the file
            </div>
          </div>
        </div>

        {/* Search bar */}
        <div className="mb-10">
          <div className="relative border border-stone-800 bg-stone-900/40 focus-within:border-orange-600 transition-colors">
            <Search className="w-5 h-5 text-stone-500 absolute left-5 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.5} />
            <input
              data-testid="library-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="What are you craving?  mackerel, chocolate, a wet Tuesday..."
              className="w-full bg-transparent pl-14 pr-14 py-5 text-stone-100 placeholder-stone-600 outline-none font-serif-display text-xl md:text-2xl"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                data-testid="library-search-clear"
                aria-label="Clear search"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-500 hover:text-orange-500 transition-colors"
              >
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.24em] text-stone-500 mr-1">Try</span>
            {cravings.map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`library-craving-${c}`}
                onClick={() => setQ(c)}
                className="text-xs px-3 py-1 border border-stone-800 text-stone-400 hover:border-orange-600 hover:text-orange-500 transition-colors font-mono-accent"
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="space-y-5 mb-14">
          <FilterRow label="Season" options={seasonFilters} value={season} onChange={setSeason} testidPrefix="season" />
          <FilterRow label="Course" options={courseFilters} value={course} onChange={setCourse} testidPrefix="course" />
          <FilterRow
            label="Region"
            options={REGIONS.map((r) => r.value)}
            labels={REGIONS.reduce((a, r) => ({ ...a, [r.value]: r.label }), {})}
            value={region}
            onChange={setRegion}
            testidPrefix="region"
          />
          <FilterRow
            label="From"
            options={sourceFilters.map((s) => s.value)}
            labels={sourceFilters.reduce((a, s) => ({ ...a, [s.value]: s.label }), {})}
            value={source}
            onChange={setSource}
            testidPrefix="source"
          />
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-3">
            <span className="eyebrow min-w-[80px]">Diet</span>
            <button
              type="button"
              data-testid="filter-vegetarian-toggle"
              onClick={() =>
                setVegetarianOnly((v) => {
                  const next = !v;
                  if (next) setVeganOnly(false);
                  return next;
                })
              }
              className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                vegetarianOnly
                  ? "border-emerald-500 bg-emerald-950/30 text-emerald-400"
                  : "border-stone-800 text-stone-400 hover:border-stone-600 hover:text-stone-200"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              Vegetarian {vegetarianOnly && "· on"}
            </button>
            <button
              type="button"
              data-testid="filter-vegan-toggle"
              onClick={() =>
                setVeganOnly((v) => {
                  const next = !v;
                  if (next) setVegetarianOnly(false);
                  return next;
                })
              }
              className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                veganOnly
                  ? "border-lime-500 bg-lime-950/30 text-lime-400"
                  : "border-stone-800 text-stone-400 hover:border-stone-600 hover:text-stone-200"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-lime-400" />
              Fully vegan {veganOnly && "· on"}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
          </div>
        ) : visibleRecipes.length === 0 ? (
          <div className="text-center py-32 border border-stone-800">
            <div className="magazine-number text-8xl mb-6">Ø</div>
            <p className="font-serif-display italic text-2xl text-stone-400">
              Nothing matches. Try loosening the filters, or ask the kitchen for something new.
            </p>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="library-grid">
              {shownRecipes.map((r, i) => (
                <RecipeCard key={r.id} recipe={r} index={i} />
              ))}
            </div>
            {usePagination && visibleCap < visibleRecipes.length && (
              <div className="mt-16 flex flex-col items-center gap-3">
                <span className="text-[10px] uppercase tracking-[0.24em] text-stone-500">
                  Showing {shownRecipes.length} of {visibleRecipes.length}
                </span>
                <button
                  type="button"
                  onClick={() => setVisibleCap((v) => v + 48)}
                  data-testid="library-load-more"
                  className="inline-flex items-center gap-3 border border-orange-800 hover:border-orange-500 text-orange-500 hover:text-orange-400 px-8 py-3.5 uppercase text-xs tracking-[0.24em] font-medium transition-colors"
                >
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const FilterRow = ({ label, options, value, onChange, testidPrefix, labels = {} }) => (
  <div className="flex flex-wrap items-baseline gap-x-6 gap-y-3">
    <span className="eyebrow min-w-[80px]">{label}</span>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const active = value === o;
        return (
          <button
            key={o}
            data-testid={`filter-${testidPrefix}-${o}`}
            onClick={() => onChange(o)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
              active
                ? "border-orange-600 bg-orange-700/20 text-orange-500"
                : "border-stone-800 text-stone-400 hover:border-stone-600 hover:text-stone-200"
            }`}
          >
            {labels[o] || o}
          </button>
        );
      })}
    </div>
  </div>
);

export default Library;
